/**
 * The metric grid for an enrichment run.
 *
 * The scrape grid's seven tiles are Items / Created / HITL / Duplicate /
 * Skipped / Failed / Cost. Three of those cannot happen to a job row —
 * `created` and `duplicate` are scrape outcomes, and no job runs a dedup stage
 * — so the page reported "Created 0 · 0%" for a run that enriched everything it
 * touched. These seven are the enrichment outcome set instead.
 *
 * **Clicks filter the table by `status`, which is all
 * `GET /runs/{id}/events` accepts** (`status` and `since`, nothing else). Two
 * tiles are therefore display-only rather than approximate:
 *
 * - *Manual review* is `final_outcome='manual_review'` on a row whose status is
 *   `done`, so a status filter cannot isolate it. It has happened twice in all
 *   of prod (2026-09-02), so an honest un-clickable number beats a filter that
 *   returns every finished job.
 * - *Jobs* is the unfiltered table, which is what clearing the filter does.
 */
import {
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  ListChecks,
  ShieldQuestion,
  SkipForward,
  XCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import { MetricCard, shareOf } from "@/pages/tina/runs/components/metric-card";
import type { EnrichmentRunFacts } from "@/pages/tina/runs/lib/enrichment-run-facts";
import type { ItemStatus, RunSummaryRow } from "@/types/tina";

interface EnrichmentRunMetricsProps {
  /** Null until the run's rows land; the summary covers three tiles meanwhile. */
  facts: EnrichmentRunFacts | null;
  summary: RunSummaryRow;
  statusFilter: ItemStatus | null;
  onToggleStatus: (status: ItemStatus) => void;
  onCostClick: () => void;
}

const dash = "—";

export function EnrichmentRunMetrics({
  facts,
  summary,
  statusFilter,
  onToggleStatus,
  onCostClick,
}: EnrichmentRunMetricsProps) {
  const total = facts?.total ?? summary.event_count;
  // `hitl_count`/`skipped_count`/`failed_count` are outcome filters that mean
  // the same thing on both classes, so they render immediately and the fetched
  // rows only confirm them. `enriched` has no counterpart at all.
  const manualReview = facts?.manualReview ?? summary.hitl_count;
  const skipped = facts?.skipped ?? summary.skipped_count;
  const failed = facts?.failed ?? summary.failed_count;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
      <MetricCard
        label="Jobs"
        value={total}
        icon={<ListChecks className="h-3.5 w-3.5" />}
        tone="sky"
        hint={entityHint(facts)}
      />
      <MetricCard
        label="Enriched"
        value={facts ? facts.enriched : dash}
        icon={<CheckCircle2 className="h-3.5 w-3.5" />}
        tone="emerald"
        share={facts ? shareOf(facts.enriched, total) : null}
        onClick={() => onToggleStatus("done")}
        active={statusFilter === "done"}
      />
      <MetricCard
        label="Parked"
        value={facts ? facts.parked : dash}
        icon={<ShieldQuestion className="h-3.5 w-3.5" />}
        tone="orange"
        share={facts ? shareOf(facts.parked, total) : null}
        hint="waiting on a curator"
        onClick={() => onToggleStatus("hitl")}
        active={statusFilter === "hitl"}
      />
      <MetricCard
        label="Manual review"
        value={manualReview}
        icon={<ClipboardCheck className="h-3.5 w-3.5" />}
        tone="amber"
        share={shareOf(manualReview, total)}
        hint="park limit reached"
      />
      <MetricCard
        label="Skipped"
        value={skipped}
        icon={<SkipForward className="h-3.5 w-3.5" />}
        tone="zinc"
        share={shareOf(skipped, total)}
        onClick={() => onToggleStatus("skipped")}
        active={statusFilter === "skipped"}
      />
      <MetricCard
        label="Failed"
        value={failed}
        icon={<XCircle className="h-3.5 w-3.5" />}
        tone="red"
        share={shareOf(failed, total)}
        onClick={() => onToggleStatus("failed")}
        active={statusFilter === "failed"}
      />
      <MetricCard
        label="Cost"
        value={
          <span className={cn(!summary.total_cost && "text-muted-foreground")}>
            {formatUsdCost(summary.total_cost)}
          </span>
        }
        icon={<CircleDollarSign className="h-3.5 w-3.5" />}
        tone="indigo"
        hint="click for breakdown"
        onClick={onCostClick}
      />
    </div>
  );
}

/** "2 × Artist · 1 × Venue" — what the run was actually about. */
function entityHint(facts: EnrichmentRunFacts | null): string | undefined {
  if (!facts || facts.groups.length === 0) return undefined;
  return facts.groups
    .map((group) => `${group.entities.length} × ${group.label}`)
    .join(" · ");
}
